import axios from 'axios';
import { bool } from 'yup';

const BASE_URL = process.env.REACT_APP_BASE_URL;
interface ComplaintData {
  user_id?: number;
  category_id: number; // This will be converted to number in the form
  subject: string;
  description: string;
  status?: 'Pending' | 'Ongoing' | 'Completed';
  photo_url?: string;
}

interface ComplaintResponse {
  data: any[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

class ComplaintService {
  async getCategories() {
    try {
      const response = await axios.get(`${BASE_URL}/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Support both current and legacy response shapes.
      const categories =
        response.data?.categories ??
        response.data?.data ??
        (Array.isArray(response.data) ? response.data : []);

      return { data: categories };
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }
  async createComplaint(data: ComplaintData, photoFile: File | null) {
    let photoUrl = null;

    // If a photo was provided, upload it first
    if (photoFile) {
      const formData = new FormData();
      formData.append('photo', photoFile);

      const uploadResponse = await axios.post(
        `${BASE_URL}/uploads`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
        }
      );

      photoUrl = uploadResponse.data.url;
    }

    // Now create the complaint with the photo URL if available
    // We don't need to send user_id as it's extracted from JWT token in backend
    return axios.post(
      `${BASE_URL}/complains`,
      {
        ...data,
        photo_url: photoUrl,
      },
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    ).then(response => response.data);
  } async getMyComplaints(search = '', page = 1, limit = 10): Promise<ComplaintResponse> {
    try {
      const response = await axios.get(
        `${BASE_URL}/complains/my?search=${search}&page=${page}&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Ensure each complaint has a string ID
      const complaints = response.data.data || [];
      const formattedComplaints = complaints.map((complaint: any) => {
        // Make sure id is string - if 'id' is missing but 'complain_id' exists, use that
        if (!complaint.id && complaint.complain_id) {
          complaint.id = complaint.complain_id;
        }

        // If id is still not available, add a fallback id
        if (!complaint.id) {
          complaint.id = `fallback-${Math.random().toString(36).substring(2, 15)}`;
        }

        // Ensure id is a string
        complaint.id = String(complaint.id);

        return complaint;
      });

      return {
        data: formattedComplaints,
        totalCount: response.data.totalCount || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      };
    } catch (error) {
      console.error('Error fetching complaints:', error);
      return {
        data: [],
        totalCount: 0,
        currentPage: 1,
        totalPages: 1
      };
    }
  }
  async getComplaintDetails(id: string): Promise<any> {
    return axios.get(`${BASE_URL}/complains/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    }).then(response => response.data);
  }

  // Admin methods for managing complaints
  async getAllComplaints(search = '', page = 1, limit = 10, status?: string): Promise<any> {
    try {
      const params = new URLSearchParams({
        search,
        page: page.toString(),
        limit: limit.toString(),
      });

      if (status) {
        params.append('status', status);
      }

      const response = await axios.get(
        `${BASE_URL}/complains?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return {
        data: response.data.data || [],
        totalCount: response.data.totalCount || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      };
    } catch (error) {
      console.error('Error fetching all complaints:', error);
      return {
        data: [],
        totalCount: 0,
        currentPage: 1,
        totalPages: 1
      };
    }
  }


  // Update complaint status with a status update note
  async updateComplaintStatus(complaintId: string, status: string, updateText: string): Promise<any> {
    return axios.post(
      `${BASE_URL}/complains/${complaintId}/status`,
      {
        status: status,
        update_text: updateText
      },
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    ).then(response => response.data);
  }

  // Get complaints by category
  async getComplaintsByCategory(categoryId: number, page = 1, limit = 5): Promise<ComplaintResponse> {
    try {
      const response = await axios.get(
        `${BASE_URL}/complains/my?category_id=${categoryId}&page=${page}&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return {
        data: response.data.data || [],
        totalCount: response.data.totalCount || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      };
    } catch (error) {
      console.error('Error fetching complaints by category:', error);
      return {
        data: [],
        totalCount: 0,
        currentPage: 1,
        totalPages: 1
      };
    }
  }

  // Get complaints by category across all users
  async getCategoryComplaintsAllUsers(categoryId: number, page = 1, limit = 10): Promise<ComplaintResponse> {
    try {
      const response = await axios.get(
        `${BASE_URL}/complains/category/${categoryId}?page=${page}&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return {
        data: response.data.data || [],
        totalCount: response.data.totalCount || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1,
      };
    } catch (error) {
      console.error('Error fetching all-user complaints by category:', error);
      return {
        data: [],
        totalCount: 0,
        currentPage: 1,
        totalPages: 1,
      };
    }
  }

  // Edit complaint
  async updateComplaint(id: string, data: ComplaintData): Promise<any> {
    try {
      const response = await axios.put(
        `${BASE_URL}/complains/${id}`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating complaint:', error);
      throw error;
    }
  }

  // Delete complaint
  async deleteComplaint(id: string): Promise<any> {
    try {
      const response = await axios.delete(
        `${BASE_URL}/complains/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting complaint:', error);
      throw error;
    }
  }

  async authorizeComplaint(complaintId: string, newStatus: string): Promise<any> {
    try {
      const response = await axios.put(
        `${BASE_URL}/complains/${complaintId}/authorize`,
        { newStatus: newStatus }, // Changed to match expected property name
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error authorizing complaint:', error);
      throw error;
    }
  }

}


export default new ComplaintService();
